import { useState } from "react";
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

/**
 * Modal asking if this is an additional order or a reprint
 * Shown when starting PRINT-MACH on a job that was previously printed
 *
 * Returns object to onSelect:
 * - For additional: { type: "additional", reason: null }
 * - For reprint: { type: "reprint", reason: "internal_error" | "customer_request" }
 */
export default function ReprintModal({ job, printCount, onSelect, onCancel }) {
  const [reprintType, setReprintType] = useState(null); // "additional" | "reprint"
  const [reprintReason, setReprintReason] = useState(null); // "internal_error" | "customer_request"

  const handleTypeSelect = (type) => {
    setReprintType(type);
    if (type === "additional") {
      setReprintReason(null); // Clear reason if switching from reprint
    }
  };

  const handleSubmit = () => {
    if (!reprintType) return;
    if (reprintType === "reprint" && !reprintReason) return;

    onSelect({
      type: reprintType,
      reason: reprintType === "reprint" ? reprintReason : null,
    });
  };

  const canSubmit = reprintType === "additional" || (reprintType === "reprint" && reprintReason);

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      showCloseButton={false}
      size="md"
    >
      {/* Header */}
      <div className="-mx-4 -mt-4 px-6 py-4 border-b border-amber-200 bg-amber-50 rounded-t-lg">
        <div className="flex items-center gap-3">
          <span className="text-2xl">⚠️</span>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Job Previously Printed</h2>
            <p className="text-sm text-amber-700 mt-1">
              This job has been printed {printCount} time{printCount !== 1 ? 's' : ''} before
            </p>
          </div>
        </div>
      </div>

      {/* Job Info */}
      <div className="-mx-4 px-6 py-3 border-b border-gray-100 bg-slate-200">
        <p className="text-sm text-gray-700">
          <span className="text-gray-500">Job:</span>{" "}
          <span className="font-medium">{job?.jobNumber || job?.name || "Unknown"}</span>
        </p>
      </div>

      {/* Options */}
      <div className="py-4">
        <p className="text-sm text-gray-700 mb-4">
          Is this an additional order or a reprint?
        </p>
        <div className="space-y-3">
          {/* Additional Order Option */}
          <button
            onClick={() => handleTypeSelect("additional")}
            className={`
              w-full p-4 rounded-lg border text-left transition-all
              ${reprintType === "additional"
                ? "bg-blue-50 border-blue-500 text-blue-900"
                : "bg-white border-gray-200 text-gray-700 hover:bg-slate-200 hover:border-gray-300"
              }
            `}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">📦</span>
              <div>
                <span className="font-medium block">Additional Order</span>
                <span className={`text-xs ${reprintType === "additional" ? 'text-blue-600' : 'text-gray-500'}`}>
                  New order for more prints of this job
                </span>
              </div>
            </div>
          </button>

          {/* Reprint Option */}
          <button
            onClick={() => handleTypeSelect("reprint")}
            className={`
              w-full p-4 rounded-lg border text-left transition-all
              ${reprintType === "reprint"
                ? "bg-blue-50 border-blue-500 text-blue-900"
                : "bg-white border-gray-200 text-gray-700 hover:bg-slate-200 hover:border-gray-300"
              }
            `}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔄</span>
              <div>
                <span className="font-medium block">Reprint</span>
                <span className={`text-xs ${reprintType === "reprint" ? 'text-blue-600' : 'text-gray-500'}`}>
                  Reprinting due to issue or customer request
                </span>
              </div>
            </div>
          </button>

          {/* Reprint Reason Sub-Options (shown when Reprint is selected) */}
          {reprintType === "reprint" && (
            <div className="ml-6 pl-4 border-l-2 border-blue-300 space-y-2 mt-2">
              <p className="text-xs text-gray-500 mb-2">What's the reason for reprinting?</p>

              {/* Internal Error */}
              <button
                onClick={() => setReprintReason("internal_error")}
                className={`
                  w-full p-3 rounded-lg border text-left transition-all
                  ${reprintReason === "internal_error"
                    ? "bg-red-50 border-red-500 text-red-900"
                    : "bg-white border-gray-200 text-gray-700 hover:bg-slate-200 hover:border-gray-300"
                  }
                `}
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">🏭</span>
                  <div>
                    <span className="font-medium text-sm block">Internal Error</span>
                    <span className={`text-xs ${reprintReason === "internal_error" ? 'text-red-600' : 'text-gray-500'}`}>
                      Our mistake - machine issue, operator error, etc.
                    </span>
                  </div>
                </div>
              </button>

              {/* Customer Request */}
              <button
                onClick={() => setReprintReason("customer_request")}
                className={`
                  w-full p-3 rounded-lg border text-left transition-all
                  ${reprintReason === "customer_request"
                    ? "bg-amber-50 border-amber-500 text-amber-900"
                    : "bg-white border-gray-200 text-gray-700 hover:bg-slate-200 hover:border-gray-300"
                  }
                `}
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">👤</span>
                  <div>
                    <span className="font-medium text-sm block">Customer Request</span>
                    <span className={`text-xs ${reprintReason === "customer_request" ? 'text-amber-600' : 'text-gray-500'}`}>
                      Customer changed mind, artwork update, etc.
                    </span>
                  </div>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          Continue
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
